import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { Alert, Box, IconButton, Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import { updateDcaBotBaseCurrencyAmountTargets } from "API/calls";
import BackdropLoading from "components/elements/BackdropLoading";
import Modal from "components/elements/Modal";
import PriceField from "components/elements/PriceField";
import { FieldArray, FormikProvider, useFormik } from "formik";
import useExchangesMetadata from "hooks/useExchangesMetadata";
import { useSnackbar } from "notistack";
import { Trans, useTranslation } from "react-i18next";
import { renderNumber } from "utils/formatter";
import * as yup from "yup";

const MAX_TARGETS = 10;
const BTC_MAX_AMOUNT = 1_000_000;

interface IProps {
  id: string;
  isOpen: boolean;
  baseCurrency: string;
  exchangeEnum: string;
  currencyPair: string;
  currentAmount: number;
  baseCurrencyAmountTargets: number[];
  handleClose: () => void;
  refreshData: () => void;
}

const BaseCurrencyAmountTargetsModal = ({
  id,
  isOpen,
  baseCurrency,
  exchangeEnum,
  currencyPair,
  currentAmount,
  baseCurrencyAmountTargets,
  handleClose,
  refreshData,
}: IProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const { baseCurrScale } = useExchangesMetadata(exchangeEnum, currencyPair);

  const isBTC = baseCurrency === "BTC";

  const formik = useFormik({
    initialValues: {
      targets:
        baseCurrencyAmountTargets.length > 0 ? baseCurrencyAmountTargets.map((v) => String(v).replace(".", ",")) : [""],
    },
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      targets: yup
        .array()
        .of(
          yup
            .string()
            .required(t("fieldRequired"))
            .test(
              "greaterThanZero",
              t("chartHistory.baseCurrencyAmountChart.modal.validation.greaterThanZero"),
              (val) => {
                if (!val) return false;
                const num = Number(val.replace(",", "."));
                return num > 0;
              }
            )
            .test("btcMax", t("chartHistory.baseCurrencyAmountChart.modal.validation.btcMax"), function (val) {
              if (!isBTC || !val) return true;
              const num = Number(val.replace(",", "."));
              return num <= BTC_MAX_AMOUNT;
            })
        )
        .test("unique", "", function (values) {
          if (!values) return true;
          const parsed = values.map((v) => Number((v ?? "").replace(",", ".")));
          const errors: yup.ValidationError[] = [];
          parsed.forEach((val, idx) => {
            if (parsed.indexOf(val) !== idx && val > 0) {
              errors.push(
                this.createError({
                  path: `targets[${idx}]`,
                  message: t("chartHistory.baseCurrencyAmountChart.modal.validation.duplicate"),
                })
              );
            }
          });
          if (errors.length > 0) throw new yup.ValidationError(errors);

          return true;
        }),
    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        const targets = values.targets.map((v) => Number(v.replace(",", "."))).filter((v) => v > 0);
        await updateDcaBotBaseCurrencyAmountTargets(id, targets);
        refreshData();
        handleClose();
        enqueueSnackbar(t("chartHistory.baseCurrencyAmountChart.modal.success"), { variant: "success" });
      } catch (error: any) {
        enqueueSnackbar(t("chartHistory.baseCurrencyAmountChart.modal.error"), { variant: "error" });
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleRemoveTarget = (index: number) => {
    const newTargets = [...formik.values.targets];
    newTargets.splice(index, 1);
    formik.setFieldValue("targets", newTargets);
  };

  return (
    <Modal
      open={isOpen}
      size="xs"
      onCancel={handleClose}
      withCloseButton
      title={t("chartHistory.baseCurrencyAmountChart.modal.title", { baseCurrency })}
      customConfirmButton={
        <Button disabled={isLoading || !formik.dirty} variant="contained" onClick={() => formik.handleSubmit()}>
          {t("update")}
        </Button>
      }>
      <Stack spacing={2}>
        <Alert severity="info" sx={{ "@media (max-width:400px)": { fontSize: "0.75rem" } }}>
          {t("chartHistory.baseCurrencyAmountChart.modal.info")}
        </Alert>

        <Typography variant="subtitle2">
          <Trans
            i18nKey={"chartHistory.baseCurrencyAmountChart.modal.currentAmount"}
            values={{
              amount: renderNumber(currentAmount, baseCurrScale, false),
              baseCurrency,
            }}
          />
        </Typography>

        <FormikProvider value={formik}>
          <FieldArray name="targets">
            {({ push }) => (
              <Stack spacing={1.5}>
                {formik.values.targets.map((_, index) => (
                  <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PriceField
                      autoComplete="off"
                      precision={baseCurrScale}
                      fullWidth
                      onlyPositive
                      allowZero
                      id={`targets.${index}`}
                      name={`targets.${index}`}
                      label={`${t("chartHistory.baseCurrencyAmountChart.modal.targetLabel")} ${index + 1}`}
                      value={formik.values.targets[index]}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={Boolean(
                        Array.isArray(formik.errors.targets)
                          && formik.errors.targets[index]
                          && Array.isArray(formik.touched.targets)
                          && formik.touched.targets[index]
                      )}
                      helperText={
                        Array.isArray(formik.errors.targets)
                        && Array.isArray(formik.touched.targets)
                        && formik.touched.targets[index]
                          ? (formik.errors.targets[index] as string)
                          : undefined
                      }
                      InputProps={{ endAdornment: <InputAdornment position="end">{baseCurrency}</InputAdornment> }}
                    />
                    <IconButton size="small" color="error" onClick={() => handleRemoveTarget(index)}>
                      <HighlightOffIcon />
                    </IconButton>
                  </Box>
                ))}
                {formik.values.targets.length < MAX_TARGETS && (
                  <Button
                    variant="contained"
                    size="small"
                    color="info"
                    startIcon={<AddIcon />}
                    onClick={() => push("")}
                    sx={{ alignSelf: "flex-start" }}>
                    {t("chartHistory.baseCurrencyAmountChart.modal.addTarget", {
                      count: formik.values.targets.length + 1,
                    })}
                  </Button>
                )}
              </Stack>
            )}
          </FieldArray>
        </FormikProvider>
      </Stack>
      <BackdropLoading open={isLoading} />
    </Modal>
  );
};

export default BaseCurrencyAmountTargetsModal;
